/**
 * Centralised formatters for the analytics surface. Replaces the per-file
 * `euro / int / pct / days / months` helpers that had drifted across pages
 * (some trimmed to 1 decimal, others rounded; some handled null, others
 * crashed on NaN). Exported here so every chart, KPI and table reads the
 * same numbers identically.
 *
 * Convention: every formatter accepts `number | null | undefined`, returns
 * the em-dash fallback (`—`) for missing / non-finite values, and never
 * throws.
 */

const DASH = '—'

function isFinite(value: number | null | undefined): value is number {
  return typeof value === 'number' && Number.isFinite(value)
}

/** Compact currency with adaptive scale (€1.2M / €850k / €1,234). */
export function euro(value: number | null | undefined): string {
  if (!isFinite(value)) return DASH
  if (value >= 1_000_000_000) return `€${(value / 1_000_000_000).toFixed(2)}B`
  if (value >= 1_000_000) return `€${(value / 1_000_000).toFixed(1)}M`
  if (value >= 1_000) return `€${(value / 1_000).toFixed(0)}k`
  return `€${Math.round(value).toLocaleString()}`
}

/** Euro with thousand-separated integer digits (no scaling). Used on €/m². */
export function euroInt(value: number | null | undefined): string {
  if (!isFinite(value)) return DASH
  return `€${Math.round(value).toLocaleString()}`
}

/** Thousand-separated integer. */
export function int(value: number | null | undefined): string {
  if (!isFinite(value)) return DASH
  return Math.round(value).toLocaleString()
}

/** Percentage with a configurable decimal count (default 1). */
export function pct(value: number | null | undefined, digits = 1): string {
  if (!isFinite(value)) return DASH
  return `${value.toFixed(digits)}%`
}

/** Days, rounded with "d" suffix. */
export function days(value: number | null | undefined): string {
  if (!isFinite(value)) return DASH
  return `${Math.round(value)}d`
}

/** Months with one decimal ("6.0", "12.4") — absorption indicator. */
export function months(value: number | null | undefined): string {
  if (!isFinite(value)) return DASH
  return value.toFixed(1)
}

/** Fixed-precision number for general stats (eg median €/m² rounded). */
export function num(value: number | null | undefined, digits = 0): string {
  if (!isFinite(value)) return DASH
  return value.toLocaleString(undefined, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  })
}

/** Signed delta like "+12.4%" / "-3.0%" — used on WoW/PoP chips. */
export function signedPct(value: number | null | undefined, digits = 1): string {
  if (!isFinite(value)) return DASH
  const prefix = value > 0 ? '+' : ''
  return `${prefix}${value.toFixed(digits)}%`
}
